import React, { ReactElement, useEffect, useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { getDocumentPreviewUrl } from '../../../store/knowledge/actions'
import DocEmpty from '../../../assets/New-images/doc-empty.svg'
import greenCheck from '../../../assets/New-images/green-check.svg'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { isTextBaseFile } from '@/utils/appUtils'

const reactPdf = require('react-pdf/dist/esm/entry.webpack')
const { Document, Page } = reactPdf

interface Props {
  documentId?: string
  qnaScreen?: boolean
  setUrlMapForParent?: any
  getNoOfPages?: any
  multiDoc?: boolean
  pageWidth?: number
  style?: React.CSSProperties
  onPageChange?: (pageNumber: number) => void
  spreadView?: boolean
}

export default function PDFViewer({
  documentId,
  qnaScreen,
  setUrlMapForParent,
  multiDoc,
  getNoOfPages,
  pageWidth,
  style,
  onPageChange,
  spreadView = false,
}: Props): ReactElement {
  const { t } = useTranslation('translation')
  const dispatch = useDispatch()
  const [numPages, setNumPages] = useState(2)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageInputValue, setPageInputValue] = useState('1')
  const [isLandscape, setIsLandscape] = useState<boolean | null>(null)
  const [urlMap, setUrlMap] = useState({
    name: '',
    url: '',
    type: '',
  })
  const [loading, setLoading] = useState(false)
  const [documentLoadError, setDocumentLoadError] = useState(false)
  const [savedScrollPosition, setSavedScrollPosition] = useState<{
    x: number
    y: number
  } | null>(null)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [flipDirection, setFlipDirection] = useState<'next' | 'prev' | null>(
    null,
  )
  let source = axios.CancelToken.source()

  useEffect(() => {
    getPreviewUrl(documentId)
    return () => {
      source.cancel('Cancelled from PDFviewer!')
    }
  }, [documentId])

  const getPreviewUrl = async (documentId: string) => {
    let temp: any = {}
    let tempObjForParent: any = {}
    setDocumentLoadError(false)
    setLoading(true)
    try {
      let previewUrlResponse: any = await dispatch(
        getDocumentPreviewUrl(documentId, source),
      )

      if (
        previewUrlResponse.data &&
        previewUrlResponse.data.document.preview_url
      ) {
        const fileObject = {
          name: previewUrlResponse.data.document.name,
          url: previewUrlResponse.data.document.preview_url,
          type: previewUrlResponse.data.document.preview_url
            .split('?')[0]
            .split('.')[
            previewUrlResponse.data.document.preview_url.split('.').length - 1
          ],
        }
        tempObjForParent[documentId] = { ...fileObject }
        temp = { ...fileObject }
        source.cancel('File preview cancelled from source.')
      } else {
        tempObjForParent[documentId] = {
          name: previewUrlResponse.data.document.name,
          url: null,
          type: previewUrlResponse.data.document.name.split('.').pop(),
        }
        temp = {
          name: previewUrlResponse.data.document.name,
          url: null,
          type: previewUrlResponse.data.document.name.split('.').pop(),
        }
        setTimeout(() => {
          getPreviewUrl(documentId)
        }, 3000)
      }
      setUrlMapForParent && setUrlMapForParent(tempObjForParent)
      if (temp?.url) {
        setLoading(false)
      }
      setUrlMap(temp)
    } catch (error) {
      setLoading(false)
      setDocumentLoadError(true)
    }
  }

  const onDocumentLoadSuccess = (data: any) => {
    getNoOfPages && getNoOfPages(data._pdfInfo.numPages || 0)
    setNumPages(data._pdfInfo.numPages)
  }

  const onDocumentLoadError = () => {
    setDocumentLoadError(true)
  }

  const saveScrollPosition = useCallback(() => {
    const scrollableParent = document.querySelector(
      '.page-mid-wraper',
    ) as HTMLElement

    if (scrollableParent) {
      setSavedScrollPosition({
        x: scrollableParent.scrollLeft,
        y: scrollableParent.scrollTop,
      })
    }
  }, [])

  const restoreScrollPosition = useCallback(() => {
    if (!savedScrollPosition) return

    requestAnimationFrame(() => {
      if (!savedScrollPosition) return

      // Find the page-mid-wraper which is the main scrollable container
      const scrollableParent = document.querySelector(
        '.page-mid-wraper',
      ) as HTMLElement

      if (scrollableParent) {
        scrollableParent.scrollTop = savedScrollPosition.y
        scrollableParent.scrollLeft = savedScrollPosition.x
      }
      setSavedScrollPosition(null)
    })
  }, [savedScrollPosition])

  const onPageLoadSuccess = (page: any) => {
    try {
      if (
        isLandscape === null &&
        page &&
        typeof page.getViewport === 'function'
      ) {
        const viewport = page.getViewport({ scale: 1 })
        if (viewport?.width && viewport?.height) {
          setIsLandscape(viewport.width > viewport.height)
        }
      }
    } catch (e) {
      setIsLandscape(prev => (prev === null ? null : prev))
    }

    restoreScrollPosition()
    // End transition after page loads
    setIsTransitioning(false)
    setFlipDirection(null)
  }

  const changePage = (direction: 1 | -1) => {
    if (isTransitioning) return // Prevent rapid clicking

    saveScrollPosition()
    setIsTransitioning(true)
    setFlipDirection(direction === 1 ? 'next' : 'prev')

    setPageNumber(prevPageNumber => {
      if (!numPages) return prevPageNumber

      const newPage = Math.min(
        Math.max(prevPageNumber + direction, 1),
        numPages,
      )
      setPageInputValue(String(newPage))
      return newPage
    })
  }

  const goToPage = (targetPage: number) => {
    if (isTransitioning) return // Prevent rapid clicking

    saveScrollPosition()
    if (targetPage >= 1 && targetPage <= numPages) {
      setFlipDirection(targetPage > pageNumber ? 'next' : 'prev')
      setIsTransitioning(true)
      setPageNumber(targetPage)
      setPageInputValue(String(targetPage))
    } else {
      // Reset to current page if invalid
      setPageInputValue(String(pageNumber))
    }
  }

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInputValue(e.target.value)
  }

  const handlePageInputBlur = () => {
    const value = parseInt(pageInputValue, 10)
    if (!isNaN(value)) {
      goToPage(value)
    } else {
      setPageInputValue(String(pageNumber))
    }
  }

  const handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur()
    }
  }

  useEffect(() => {
    // Update input value when pageNumber changes externally
    setPageInputValue(String(pageNumber))
  }, [pageNumber])

  useEffect(() => {
    // Notify parent when page number changes
    if (onPageChange && pageNumber) {
      onPageChange(pageNumber)
    }
  }, [pageNumber, onPageChange])

  const previousPage = () => {
    changePage(-1)
  }

  const nextPage = () => {
    changePage(1)
  }

  return (
    <>
      <div className="pdf-viewer-wrapper" style={style}>
        {urlMap && urlMap.type === 'pdf' && (
          <React.Fragment>
            <Document
              key={`${documentId || ''}-${urlMap?.url || 'no-url'}`}
              file={urlMap && urlMap.url ? urlMap.url : ''}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
            >
              <div
                className={`pdf-pages-container ${isTransitioning ? `flipping flipping-${flipDirection}` : ''}`}
                style={{
                  display: 'flex',
                  gap: '0',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  flexWrap: 'nowrap',
                }}
              >
                <Page
                  key={`${pageNumber}-${urlMap?.url || 'no-url'}-left`}
                  pageNumber={pageNumber}
                  loading={t('Loading page...')}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  width={
                    pageWidth
                      ? spreadView && isLandscape === false
                        ? pageWidth / 2
                        : pageWidth
                      : undefined
                  }
                  onLoadSuccess={onPageLoadSuccess}
                />
                {/* Divider line between spread pages */}
                {spreadView &&
                  isLandscape === false &&
                  pageNumber + 1 <= numPages && (
                    <div
                      className="spread-divider"
                      style={{
                        width: '2px',
                        backgroundColor: '#e0e0e0',
                        alignSelf: 'stretch',
                        boxShadow: '0 0 4px rgba(0,0,0,0.1)',
                      }}
                    />
                  )}
                {spreadView &&
                  isLandscape === false &&
                  pageNumber + 1 <= numPages && (
                    <Page
                      key={`${pageNumber + 1}-${urlMap?.url || 'no-url'}-right`}
                      pageNumber={pageNumber + 1}
                      loading={t('Loading page...')}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                      width={pageWidth ? pageWidth / 2 : undefined}
                      onLoadSuccess={onPageLoadSuccess}
                    />
                  )}
              </div>
            </Document>
            <div className="pdf-viewer-page-controls">
              {(() => {
                const isPortrait = spreadView && isLandscape === false
                const lastStartPage =
                  numPages && isPortrait
                    ? numPages % 2 === 0
                      ? numPages - 1
                      : numPages
                    : numPages
                const disablePrev = pageNumber <= 1
                const disableNext =
                  !numPages ||
                  (!isPortrait && pageNumber >= numPages) ||
                  (isPortrait && pageNumber >= (lastStartPage || numPages))
                return (
                  <>
                    <button
                      type="button"
                      disabled={disablePrev}
                      onClick={previousPage}
                    >
                      <svg
                        width="6.707"
                        height="12.414"
                        viewBox="0 0 6.707 12.414"
                      >
                        <path
                          data-name="Icon feather-chevron-down"
                          d="M0,0,5.5,5.5,11,0"
                          transform="translate(6 0.707) rotate(90)"
                          fill="none"
                          stroke="rgba(9,35,230,0.5)"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="1"
                        />
                      </svg>
                    </button>
                    <div
                      className="page-number-indicator"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <input
                        type="number"
                        value={pageInputValue}
                        onChange={handlePageInputChange}
                        onBlur={handlePageInputBlur}
                        onKeyDown={handlePageInputKeyDown}
                        min={1}
                        max={numPages || undefined}
                        className="page-number-input"
                        style={{
                          width: 'auto',
                          minWidth: '40px',
                          maxWidth: '60px',
                          textAlign: 'center',
                          border: '1px solid rgba(9,35,230,0.3)',
                          padding: '2px 4px',
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          backgroundColor: 'white',
                          outline: 'none',
                          height: '22px',
                          lineHeight: '1',
                          boxSizing: 'border-box',
                        }}
                        onWheel={e => e.currentTarget.blur()}
                      />
                      <span>of {numPages || '--'}</span>
                    </div>
                    <button
                      type="button"
                      disabled={disableNext}
                      onClick={nextPage}
                    >
                      <svg
                        width="6.707"
                        height="12.414"
                        viewBox="0 0 6.707 12.414"
                      >
                        <path
                          data-name="Icon feather-chevron-down"
                          d="M9,19l5.5-5.5L20,19"
                          transform="translate(19.707 -8.293) rotate(90)"
                          fill="none"
                          stroke="rgba(9,35,230,0.5)"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="1"
                        />
                      </svg>
                    </button>
                  </>
                )
              })()}
            </div>
          </React.Fragment>
        )}

        {urlMap && !urlMap.url && multiDoc && !isTextBaseFile(urlMap.name) && (
          <div className="pdf-viewer-wrapper mt-2">
            <div className="empty-doc-msg d-flex align-items-center justify-content-center">
              <div className="text-center">
                <span className="doc-loader"></span>
                <h3>{t('Preview Loading. Please wait')}</h3>
                {/* {t("Loading times varies depending on file size")} */}
              </div>
            </div>
          </div>
        )}

        {urlMap &&
          !['pdf', 'docx', 'pptx'].includes(urlMap.type) &&
          !isTextBaseFile(urlMap.name) &&
          multiDoc &&
          !loading && (
            <div className="pdf-viewer-wrapper mt-2">
              <div className="empty-doc-msg d-flex align-items-center justify-content-center">
                <div className="text-center">
                  {/* {loading && <span className="doc-loader"></span>} */}
                  <h3>
                    {documentLoadError ||
                    !urlMap.url ||
                    isTextBaseFile(urlMap.name)
                      ? t('Preview is currently unavailable at the moment')
                      : t('Preview Loading. Please wait')}
                  </h3>
                  {/* {t("Loading times varies depending on file size")} */}
                </div>
              </div>
            </div>
          )}

        {urlMap &&
          !['pdf', 'docx', 'pptx'].includes(urlMap.type) &&
          !multiDoc &&
          !loading && (
            <div className="pdf-viewer-wrapper mt-2">
              <div className="empty-doc-msg d-flex align-items-center justify-content-center">
                <div className="text-center">
                  <h3>
                    {documentLoadError || !urlMap.url
                      ? t('Preview is currently unavailable at the moment')
                      : t(
                          'Preview is currently unavailable for this document type',
                        )}
                  </h3>
                  <figure className="my-3">
                    <img src={DocEmpty} alt="" />
                  </figure>
                  <h3>
                    {t(
                      'We have taken note of this issue and is working on fixing this.',
                    )}
                    <br />
                    {t('We seek for your understanding.')}
                  </h3>
                </div>
              </div>
            </div>
          )}
      </div>

      {((loading && !documentLoadError) ||
        (!loading && !documentLoadError && urlMap && qnaScreen)) && (
        <div className="doc-processing d-flex flex-wrap align-items-center">
          <div className="col-status">
            {loading && !documentLoadError && (
              <span className="doc-loader"></span>
            )}
            {!loading && !documentLoadError && urlMap && qnaScreen && (
              <span className="doc-success">
                <img src={greenCheck} alt="" />
              </span>
            )}
          </div>
          <div className="col-msg pl-3">
            {loading
              ? t('Walter is still reading through your document...')
              : !loading && urlMap
                ? qnaScreen
                  ? t('Your document is ready for processing!')
                  : ''
                : ''}
          </div>
        </div>
      )}
    </>
  )
}
